import { Member } from '@sendbird/chat/groupChannel';
import * as Sentry from '@sentry/react';

// Legacy chat channels carry no Sendbird custom_type, while engagement channels are tagged
// custom_type='engagement' (API-side contract; see ENG-5503). Sendbird treats '' in
// customTypesFilter as "channels with no custom type", so this include list keeps engagement
// channels out of the legacy 2-party channel list.
//
// The API pins the same value as sendbird.CustomTypeChat and scopes the Messages unread
// badge to it (clients/sendbird/sendbird.go GetChatUnreadCount, ENG-5528). Change one side
// and the badge starts counting channels this list hides — change both together.
export const LEGACY_CHAT_CUSTOM_TYPES = [''];

// True for a legacy 1:1 chat channel (untyped custom_type). Engagement channels
// (custom_type='engagement') are not legacy and must never open in the legacy Messenger,
// even via a direct /messaging/:id deep link that bypasses the list query filter (ENG-5503).
export function isLegacyChatChannel(channel: { customType: string }): boolean {
  return LEGACY_CHAT_CUSTOM_TYPES.includes(channel.customType);
}

export function findOtherMember(members: Member[], viewerId: string): Member | null {
  const others = members.filter((m) => m.userId !== viewerId);
  return others.length === 1 ? others[0] : null;
}

// Channels already reported this session. findOtherMember is called during render, so
// without this a single unresolvable channel would emit an event on every re-render.
const reportedUnresolvableChannels = new Set<string>();

// Exported for tests only — the module-level dedup would otherwise leak between cases.
export function resetUnresolvableChannelReports(): void {
  reportedUnresolvableChannels.clear();
}

// Reports a channel dropped from the Messages list because its counterpart could not be
// resolved — a legacy 1:1 chat whose other member was deleted. ENG-5503 made that
// non-fatal, which stopped the crash but made the channel vanish with no signal at all,
// leaving "my conversation disappeared" indistinguishable from correct engagement-channel
// filtering (ENG-5528 part B).
//
// Deliberate custom-type filtering stays silent: an engagement channel reaching the drop
// site is the filter working, not a fault, so only legacy channels report.
//
// Level is `warning` — a real Sentry event, deliberately not a breadcrumb. Breadcrumbs
// only surface attached to a later error, and the whole point of ENG-5503 is that there
// is no later error here; a breadcrumb would never be seen. `error` would overstate it:
// the page renders correctly and the user is not blocked, so this is expected-but-notable
// and should be searchable, not paged on.
// Chat surfaces already reported this session, keyed surface:reason:userId. The callers
// report from an effect, so the dedup is not guarding re-renders — it guards remounts. The
// same counterpart is mounted again on every return to a channel, and once per message it
// sent as the list virtualises, which without this would be one event apiece.
const reportedUnresolvableUsers = new Set<string>();

// Exported for tests only — the module-level dedup would otherwise leak between cases.
export function resetUnresolvableUserReports(): void {
  reportedUnresolvableUsers.clear();
}

// Reports a chat counterpart the api would not resolve (auth.user_not_found). ENG-5628
// stopped that error from throwing, which is right — the account is usually just gone —
// but silence would be wrong for two reasons. The api collapses permission denials into
// the SAME code (api/gql/service/schema/user_gql.go:1113-1117, "preserve legacy
// behavior"), so an authorization regression that hides live counterparts would look
// identical to a deletion and reach nobody. And a mass-deletion or api regression would
// show every user a calm "User not available" with no signal at all.
//
// Level and shape deliberately mirror reportUnresolvableChannelMember above: `warning`,
// because the pane renders and the user is not blocked — searchable, not paged on. A real
// event rather than a breadcrumb for the same reason given there: nothing throws any more,
// so there is no later error for a breadcrumb to ride on.
//
// `reason` keeps the two ways a counterpart goes unresolved separable, because they have
// different causes and different fixes: `user_not_found` is the api refusing the lookup —
// a deleted account, or the permission denial folded into the same code — while
// `no_profile` is the api returning a user that carries no profile at all. Collapsing them
// into one tag would make a profile-shaped api regression read as a wave of deletions.
export function reportUnresolvableChatUser(
  surface: 'channel_list' | 'chat_header' | 'message',
  userId: string,
  reason: 'user_not_found' | 'no_profile' = 'user_not_found'
): void {
  const key = `${surface}:${reason}:${userId}`;
  if (reportedUnresolvableUsers.has(key)) return;
  reportedUnresolvableUsers.add(key);

  Sentry.captureMessage('Chat counterpart could not be resolved', {
    level: 'warning',
    tags: { area: 'messaging', reason, surface },
    // Opaque user id only — no profile data, no message content.
    extra: { userId },
  });
}

export function reportUnresolvableChannelMember(channel: {
  url: string;
  customType: string;
  members: Member[];
}): void {
  if (!isLegacyChatChannel(channel)) return;
  if (reportedUnresolvableChannels.has(channel.url)) return;
  reportedUnresolvableChannels.add(channel.url);

  Sentry.captureMessage('Legacy chat channel hidden: counterpart could not be resolved', {
    level: 'warning',
    tags: { area: 'messaging', reason: 'unresolvable_membership' },
    // Channel URL and member count only — no message content, no profile data.
    extra: { channelUrl: channel.url, memberCount: channel.members.length },
  });
}
